import { useEffect, useRef } from "react";
import { Segmented } from "../../components/ui";
import { useOwner } from "../../state/account";
import { useRecentTrades } from "../../state/market";
import { usePref, setPref } from "../../state/prefs";
import { ago } from "../../lib/time";
import { fmt, MINUS, toRaw } from "../../lib/num";
import { shortAddress } from "../../wallet/address";

/** The venue's latest trades, newest first; new ones flash in once. */
export function Tape({ symbol }: { symbol: string }) {
  const trades = useRecentTrades().data ?? [];
  const owner = useOwner();
  const mine = usePref("tapeMine");
  const seen = useRef<Set<string> | null>(null);

  const rows = trades.filter((trade) => (mine ? owner !== null && trade.wallet === owner : true));
  const fresh = new Set<string>();
  if (seen.current) for (const trade of rows) if (!seen.current.has(trade.seq)) fresh.add(trade.seq);
  useEffect(() => {
    seen.current = new Set(trades.map((trade) => trade.seq));
  }, [trades]);

  return (
    <section className="panel panel-flush tp">
      <header className="tp-head">
        <h2 className="panel-title">Tape</h2>
        <Segmented<"all" | "mine">
          label="Whose trades"
          size="sm"
          value={mine ? "mine" : "all"}
          onChange={(value) => setPref("tapeMine", value === "mine")}
          options={[
            { value: "all", label: "Venue" },
            { value: "mine", label: "Mine" },
          ]}
        />
      </header>
      <div className="tp-body">
        {rows.length === 0 && <div className="tp-empty label">{mine ? "None of your trades in the latest fifty" : "Waiting for trades"}</div>}
        {rows.map((trade) => {
          const pnl = trade.pnlUsd ? toRaw(trade.pnlUsd, 8) : null;
          const kind = trade.kind === "liquidation" ? "liq" : trade.kind;
          return (
            <div
              key={trade.seq}
              className={`tp-row ${fresh.has(trade.seq) ? "is-fresh" : ""} ${trade.kind === "liquidation" ? "is-liq" : ""} ${trade.instrument === symbol ? "" : "is-other"}`}
              title={`${shortAddress(trade.wallet)} · ${trade.kind}`}
            >
              <span className={`tp-side ${trade.isLong ? "up" : "down"}`}>
                {trade.isLong ? "▲" : "▼"} {trade.instrument}
              </span>
              <span className="num tp-lev">{trade.leverage}×</span>
              <span className="num tp-size">{fmt(trade.notionalUsd, { places: 0, prefix: "$", compact: 4 })}</span>
              <span className={`num tp-kind ${kind === "liq" ? "down" : "muted"}`}>
                {kind === "open" ? "open" : pnl === null ? kind : (
                  <span className={pnl > 0n ? "up" : pnl < 0n ? "down" : "muted"}>
                    {pnl > 0n ? "+" : pnl < 0n ? MINUS : ""}
                    {fmt((trade.pnlUsd ?? "0").replace("-", ""), { places: pnl > -1_000_000_000n && pnl < 1_000_000_000n ? 2 : 0, prefix: "$", compact: 4 })}
                  </span>
                )}
              </span>
              <span className="num tp-age faint">{ago(trade.tsMs)}</span>
            </div>
          );
        })}
      </div>
    </section>
  );
}
